import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import HeroPhone from '@components/hero-phone';

const StyledHero = styled.section`
  position: relative;
  overflow: hidden;
  background-color: var(--ink-950);
  background-image: radial-gradient(
      900px 520px at 78% 8%,
      rgba(86, 112, 246, 0.28),
      transparent 60%
    ),
    radial-gradient(720px 520px at 6% 96%, rgba(253, 118, 255, 0.16), transparent 62%);
  color: var(--on-dark-soft);

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(70% 70% at 50% 40%, #000 20%, transparent 100%);
    -webkit-mask-image: radial-gradient(70% 70% at 50% 40%, #000 20%, transparent 100%);
    pointer-events: none;
  }

  .eyebrow {
    color: var(--blue-bright);
  }

  .inner {
    ${({ theme }) => theme.mixins.container};
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    align-items: center;
    gap: 48px;
    min-height: 100vh;
    padding-top: calc(var(--nav-height) + 40px);
    padding-bottom: 72px;

    @media (max-width: 980px) {
      grid-template-columns: 1fr;
      gap: 24px;
      min-height: auto;
      padding-top: calc(var(--nav-height) + 48px);
      padding-bottom: 64px;
      text-align: center;
    }
  }

  h1 {
    max-width: 620px;
    margin: 0 0 22px;
    color: #ffffff;
    font-size: clamp(2.3rem, 4.6vw, 3.9rem);
    line-height: 1.08;

    @media (max-width: 980px) {
      margin-left: auto;
      margin-right: auto;
    }

    .grad {
      background: var(--gradient);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }
  }

  .lead {
    max-width: 560px;
    margin: 0 0 16px;
    color: var(--on-dark);
    font-size: clamp(1.05rem, 1.6vw, 1.35rem);
    font-weight: 500;

    @media (max-width: 980px) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .desc {
    max-width: 560px;
    margin: 0 0 34px;
    color: var(--on-dark-soft);

    @media (max-width: 980px) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;

    @media (max-width: 980px) {
      justify-content: center;
    }
  }

  .primary {
    ${({ theme }) => theme.mixins.primaryButton};
  }

  .secondary {
    ${({ theme }) => theme.mixins.outlineButton};
  }

  .facts {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 28px;
    margin-top: 40px;
    padding: 0;
    list-style: none;

    @media (max-width: 980px) {
      justify-content: center;
    }

    li {
      position: relative;
      padding-left: 18px;
      color: var(--on-dark-soft);
      font-size: var(--fz-sm);

      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.55em;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--gradient);
      }
    }
  }

  .visual {
    justify-self: center;

    @media (max-width: 980px) {
      order: -1;
      max-width: 340px;
      margin: 0 auto;
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .fade-item {
      opacity: 0;
      transform: translateY(18px);
      animation: hero-rise 0.7s var(--easing) forwards;
    }
    .fade-item:nth-child(1) {
      animation-delay: 0.05s;
    }
    .fade-item:nth-child(2) {
      animation-delay: 0.14s;
    }
    .fade-item:nth-child(3) {
      animation-delay: 0.22s;
    }
    .fade-item:nth-child(4) {
      animation-delay: 0.3s;
    }
    .fade-item:nth-child(5) {
      animation-delay: 0.38s;
    }
  }

  @keyframes hero-rise {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const facts = [
  'Кандидат экономических наук',
  'Доцент ЮУрГУ',
  'Опыт более 5 лет',
  'AI и информационная безопасность',
];

const Hero = () => (
  <StyledHero id="hero">
    <div className="inner">
      <div className="text">
        <p className="eyebrow fade-item">SMART-KVADRAT · Пушкарева Мария Викторовна</p>

        <h1 className="fade-item">
          Разработка программного <span className="grad">обеспечения</span> современного уровня
        </h1>

        <p className="lead fade-item">Сайты, мобильные приложения и веб-сервисы</p>

        <div className="fade-item">
          <p className="desc">
            Профессиональная разработка и управление IT-проектами: от постановки задачи и
            проектирования решения до контроля качества, информационной безопасности и запуска.
          </p>

          <div className="actions">
            <Link className="primary" to="/contacts">
              Обсудить проект
            </Link>
            <Link className="secondary" to="/services">
              Смотреть услуги
            </Link>
          </div>

          <ul className="facts">
            {facts.map(fact => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="visual fade-item">
        <HeroPhone />
      </div>
    </div>
  </StyledHero>
);

export default Hero;
